import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { subscribe } from 'react-contextual'
import ErrorBoundary from '@argos/error-boundary'
import debounce from '@argos/utils/esnext/debounce'
import device from '@argos/utils/esnext/device'
import canUseDom from '@argos/utils/esnext/canUseDom'
import { ArrowIcon } from '@argos/icons'

import config from '../../config/config'
import { MegaMenu } from '../../containers'
import { NavLink, MegaButtons, SkipLink, MobileSectionTitle } from '../../components'
import { MegaNavService, MegaButtonsService } from '../../services'
import { NavigationStore } from '../../stores'
import { keyCodes, onHandleMenuFocus, onHandleMenuCategoryFocus } from '../../helpers/menuKeyboardControls'
import getSiteSection from '../../helpers/getSiteSection'

import styles from './Navigation.scss'

export class Navigation extends Component {
  static propTypes = {
    activeCategory: PropTypes.shape({
      index: PropTypes.number,
      title: PropTypes.string,
      link: PropTypes.string,
    }),
    activeMenu: PropTypes.number,
    focusedItem: PropTypes.number,
    keyboardActivated: PropTypes.bool,
    menuToggled: PropTypes.bool.isRequired,
    onSetKeyboardActivated: PropTypes.func,
    onToggleMenu: PropTypes.func.isRequired,
    onUpdateActiveCategory: PropTypes.func,
    onUpdateActiveMenu: PropTypes.func,
    onUpdateFocusedItem: PropTypes.func,
  }

  static defaultProps = {
    activeCategory: {
      index: null,
      title: null,
      link: null,
    },
    megaButtons: [],
    activeMenu: 0,
    focusedItem: null,
    keyboardActivated: false,
    onUpdateActiveCategory: () => {},
    onUpdateActiveMenu: () => {},
    onUpdateFocusedItem: () => {},
    onSetKeyboardActivated: () => {},
  }

  constructor(props) {
    super(props)

    this.navRef = React.createRef()
    this.shopAllRef = React.createRef()
    this.itemRefs = []
    this.menuInTimeout = 100
    this.menuExitTimeout = 50
    this.shopHoverTimer = null
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    this.handleDocumentHover = debounce(this.handleDocumentHover.bind(this), this.menuExitTimeout)
    this.handleMenuKeydown = this.handleMenuKeydown.bind(this)
  }

  get categoryIsExpanded() {
    return typeof this.props.activeCategory.index === 'number'
  }

  async componentDidMount() {
    if (!this.props.taxonomyLoaded) {
      try {
        const taxonomy = await MegaNavService({ lite: false })
        this.props.setState({ taxonomy, taxonomyLoaded: true })

        if (!this.props.megaButtons.length) {
          const megaButtons = await MegaButtonsService()
          this.props.setState({ megaButtons })
        }
      } catch (e) {
        throw new Error(e)
      }
    }
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.menuToggled && nextProps.menuToggled) {
      this.setupListeners()
    }

    if (this.props.menuToggled && !nextProps.menuToggled) {
      this.destroyMenu()
    }
  }

  setupListeners() {
    document.addEventListener('click', this.handleDocumentClick)
    document.addEventListener('mousemove', this.handleDocumentHover)
    document.addEventListener('keydown', this.handleMenuKeydown)
  }

  removeListeners() {
    document.removeEventListener('click', this.handleDocumentClick)
    document.removeEventListener('mousemove', this.handleDocumentHover)
    document.removeEventListener('keydown', this.handleMenuKeydown)
  }

  destroyMenu = (evt) => {
    this.props.onToggleMenu(evt, false, this.props.activeMenu, this.props.onUpdateActiveMenu)
    this.props.onSetKeyboardActivated(false)
    this.props.onUpdateActiveCategory({ index: null, title: null, link: null })
    this.handleNewFocusedItem(null)
    this.removeListeners()
    // return this.navRef.current.querySelector('#ShopLink').focus()
  }

  destroyCategory = () => {
    this.handleNewCategoryFocus(false)
    this.props.onUpdateActiveCategory({ index: null, title: null, link: null })
    return this.handleNewFocusedItem(this.props.focusedItem)
  }

  handleNewFocusedItem = (idx) => {
    if (typeof idx !== 'object') {
      this.itemRefs[idx].focus()
    }

    this.props.onUpdateFocusedItem(idx)
  }

  handleNewCategoryFocus = (toggle) => {
    this.categoryIsFocused = toggle
  }

  handleSavedCategoryLinks = () => {
    this.categoryImageLink = this.navRef.current.querySelector('[data-megamenu-image]')
    this.categoryTextLinks = this.navRef.current.querySelectorAll('[data-megamenu-link]')
  }

  handleMenuKeydown(evt) {
    if (
      this.categoryIsExpanded &&
      evt &&
      (evt.keyCode === keyCodes.TAB || evt.keyCode === keyCodes.ESC || evt.keyCode === keyCodes.LEFT)
    ) {
      return onHandleMenuCategoryFocus(evt, {
        shopAllRef: this.shopAllRef,
        categoryIsFocused: this.categoryIsFocused,
        categoryImageLink: this.categoryImageLink,
        categoryTextLinks: this.categoryTextLinks,
        onUpdateFocusedItem: this.handleNewFocusedItem,
        onUpdateActiveCategory: this.props.onUpdateActiveCategory,
        onToggleCategoryFocus: this.handleNewCategoryFocus,
        onSaveCategoryLinks: this.handleSavedCategoryLinks,
        onDestroyCategory: this.destroyCategory,
      })
    }

    return onHandleMenuFocus(evt, {
      focusedItem: this.props.focusedItem,
      menuLength: this.props.taxonomy.length,
      startElementTag: 'A',
      endElementTag: 'A',
      onUpdateFocusedItem: this.handleNewFocusedItem,
      onDestroy: this.destroyMenu,
      categoryIsExpanded: this.categoryIsExpanded,
    })
  }

  handleDocumentClick(evt) {
    if (!this.navRef.current.contains(evt.target) && this.props.menuToggled) {
      this.destroyMenu()
    }
  }

  handleDocumentHover(evt) {
    if (
      global.innerWidth > config.breakpoints.md &&
      !this.navRef.current.contains(evt.target) &&
      !this.props.keyboardActivated &&
      this.props.menuToggled
    ) {
      this.destroyMenu(evt)
    }
  }

  handleMenuIn = (evt) => {
    if (!this.props.menuToggled) {
      if (evt.type === 'focus') {
        if (evt.keyCode === keyCodes.ENTER) {
          this.props.onSetKeyboardActivated(true)
          this.props.onToggleMenu(evt, true, this.props.activeMenu, this.props.onUpdateActiveMenu)
          this.setupListeners()
        }
        return false
      }

      this.shopHoverTimer = setTimeout(() => {
        this.props.onToggleMenu(evt, true, this.props.activeMenu, this.props.onUpdateActiveMenu)
        this.setupListeners()
      }, this.menuInTimeout)
    }
    return false
  }

  handleMenuOut = () => {
    clearTimeout(this.shopHoverTimer)
  }

  handleShopKeyDown = (evt) => {
    if (evt.which === keyCodes.SPACE) {
      this.handleShopClick(evt)
    }
  }

  handleShopClick = (evt) => {
    evt.preventDefault()
    if (!this.props.menuToggled) {
      this.props.onSetKeyboardActivated(true)
      this.props.onToggleMenu(evt, true, this.props.activeMenu, this.props.onUpdateActiveMenu)
      this.setupListeners()
    } else {
      this.props.onSetKeyboardActivated(false)
      this.props.onToggleMenu(evt, false, this.props.activeMenu, this.props.onUpdateActiveMenu)
      this.removeListeners()
    }
  }

  handleNewMenu = (evt, level) => {
    evt.preventDefault()
    this.props.onUpdateActiveMenu(level)
  }

  handleNewCategory = (category) => {
    this.props.onUpdateFocusedItem(category.index)
    this.props.onUpdateActiveCategory(category)
  }

  handleRoute(e, type) {
    const { routes } = this.props

    if (routes[type] && typeof routes[type] === 'function') {
      e.preventDefault()
      return routes[type].call()
    }

    return false
  }

  render() {
    const { megaButtons, activeMenu, menuToggled, skipToContentTag, routes } = this.props

    // eslint-disable-next-line no-return-assign
    const setRef = (ref, idx) => (this.itemRefs[idx] = ref)

    return (
      <Fragment>
        <SkipLink skipToContentTag={skipToContentTag} />

        <nav
          className={styles.navigation}
          aria-expanded={menuToggled}
          aria-label='Categories'
          data-active-menu={activeMenu}
          ref={this.navRef}>
          {/* MegaNav Component */}
          <ul className={styles.links}>
            <MobileSectionTitle id='NavMobileTitle' text='Shop at Argos' />

            <NavLink
              id={'CategoriesLink'}
              aria={{
                hasPopup: true,
                expanded: menuToggled,
              }}
              text={'Shop all categories'}
              href={'#'}
              icon={<ArrowIcon dir='right' className={styles.rightArrowIcon} />}
              onClick={(evt) => this.handleNewMenu(evt, 1)}
            />

            <NavLink
              id={'ShopLink'}
              aria={{
                hasPopup: true,
                expanded: menuToggled,
              }}
              text={'Shop'}
              href={'#megaMenu'}
              icon={<ArrowIcon dir='down' className={styles.downArrowIcon} />}
              onClick={this.handleShopClick}
              onFocus={this.handleMenuIn}
              onKeyDown={this.handleShopKeyDown}
              onMouseEnter={canUseDom() && !device.isIpad() && this.handleMenuIn}
              onMouseLeave={canUseDom() && !device.isIpad() && this.handleMenuOut}>
              <MegaMenu
                shopAllRef={this.shopAllRef}
                onSetRef={(ref, idx) => setRef(ref, idx)}
                onUpdateActiveMenu={this.handleNewMenu}
                onUpdateActiveCategory={this.handleNewCategory}
                onToggleCategoryFocus={this.handleNewCategoryFocus}
              />
            </NavLink>

            <MegaButtons
              routes={routes}
              closeNav={(evt) => this.destroyMenu(evt)}
              links={megaButtons}
              className={styles.menuItem}
            />

            <MobileSectionTitle id='NavMobileTitle' text='Need Help?' />

            <NavLink
              id={'Stores'}
              text={'Stores'}
              href={`/stores/?clickOrigin=header:${getSiteSection()}stores`}
              onClick={(e) => this.handleRoute(e, 'stores')}
            />

            <NavLink
              id={'Help'}
              text={'Help'}
              href={`/help/?clickOrigin=header:${getSiteSection()}help`}
              onClick={(e) => this.handleRoute(e, 'help')}
            />
          </ul>
        </nav>
      </Fragment>
    )
  }
}

const SubscribedComponent = subscribe([NavigationStore], (navigation) => ({
  menuToggled: navigation.menuToggled,
  focusedItem: navigation.focusedItem,
  keyboardActivated: navigation.keyboardActivated,
  activeMenu: navigation.activeMenu,
  activeCategory: navigation.activeCategory,
  onUpdateActiveMenu: (level) => navigation.onUpdateActiveMenu(level),
  onUpdateActiveCategory: (category) => navigation.onUpdateActiveCategory(category),
  onUpdateFocusedItem: (idx) => navigation.onUpdateFocusedItem(idx),
  onToggleMenu: (evt, toggled, activeMenu, onUpdateActiveMenu) =>
    navigation.onToggleMenu(evt, toggled, activeMenu, onUpdateActiveMenu),
  onSetKeyboardActivated: (activated) => navigation.onSetKeyboardActivated(activated),
}))(subscribe()(Navigation))

const ProvidedComponent = () => (
  <ErrorBoundary>
    <SubscribedComponent />
  </ErrorBoundary>
)

export default ProvidedComponent
